import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import DeviceInfo from '@quiqupltd/qu-react-sdk/lib/utils/device-info/device-info';
import PendingCardHeader from './pending/header/OrderCardHeader';
import PendingCardBody from './pending/body/OrderCardBody';
import DeliveryFailedHeader from './delivery-failed/header';
import DeliveryFailedBody from './delivery-failed/body';
import ArrivedCardHeader from './arrived/header/ArrivedCardHeader';
import ArrivedCardBody from './arrived/body/ArrivedCardBody';
import CompletedCardHeader from './completed/header/CompletedCardHeader';
import CompletedCardBody from './completed/body/CompletedCardBody';
import Footer from '../footer/Footer';
import {
  Container,
  LoadingBar,
  CompletedStatusBar,
  ScrollContainer,
  BusinessLogoContainer,
  BusinessLogo,
} from './styles';
import {
  WAYPOINT_STATE_PENDING,
  WAYPOINT_STATE_ARRIVED,
  WAYPOINT_STATE_FINISHED,
  WAYPOINT_STATE_DELIVERY_FAILED,
  ORDER_STATE_CANCELLED,
  PICKUP,
} from '../../utils/constants';
import { orderPropTypes, customizationsPropTypes } from '../../utils/prop-types';
import { RateProvider } from '../../providers/rate.provider';
import { isCustomized } from '../../utils/customization';

/**
 * isThisCard()
 * Returns boolean if the current waypoint.state matches a certain card
 * Easily change the state of the app by replacing waypointState by one of the waypoint.state
 * @param {string} waypointState - one of ['pending', 'arrived', finished', 'delivery_failed']
 * @param {string} cardState - one of ['pending', 'arrived', finished', 'delivery_failed']
 */
const isThisCard = (waypointState, cardState) => cardState === waypointState;

// the tracking token is related to a waypoint and this waypoint.state is what defines the state of the card
export class OrderCard extends PureComponent {
  static propTypes = {
    order: orderPropTypes.isRequired,
    deviceInfo: PropTypes.shape({
      isPhone: PropTypes.bool,
    }).isRequired,
    customizations: customizationsPropTypes,
  };

  static defaultProps = {
    customizations: {},
  };

  state = {
    isOpen: !this.props.deviceInfo.isPhone || false,
  };

  handleToggleCard = () => {
    if (!this.props.deviceInfo.isPhone) {
      return;
    }

    this.setState(previousState => ({
      isOpen: !previousState.isOpen,
    }));
  };

  render() {
    const {
      order: { orderId, kind, quiqee, eta, waypoint, jobState, numberOfWaypointsAhead },
      deviceInfo,
      customizations,
    } = this.props;
    const { isOpen } = this.state;
    const { waypointType, finishedAt, state } = waypoint;
    const venueName = waypoint.waypointType === PICKUP ? get(waypoint, 'location.name', '') : '';
    const isCustomizedPartner = isCustomized(customizations);
    return (
      <Container
        isOpen={isOpen}
        isCustomized={isCustomizedPartner}
        isPending={state === WAYPOINT_STATE_PENDING}
        isDeliveryFailed={state === WAYPOINT_STATE_DELIVERY_FAILED}
        isFinished={state === WAYPOINT_STATE_FINISHED}
      >
        {customizations && customizations.logoUrl && (
          <BusinessLogoContainer>
            <BusinessLogo src={customizations.logoUrl} alt={customizations.name} />
          </BusinessLogoContainer>
        )}

        {isThisCard(state, WAYPOINT_STATE_FINISHED) && (
          <RateProvider>
            <CompletedStatusBar />
            {!isOpen && (
              <CompletedCardHeader
                date={finishedAt}
                onToggleOpenCard={this.handleToggleCard}
                type={waypointType}
                orderId={orderId}
              />
            )}
            {isOpen && (
              <ScrollContainer>
                <CompletedCardBody
                  date={finishedAt}
                  onToggleOpenCard={this.handleToggleCard}
                  type={waypointType}
                  companyName={customizations.name}
                  orderId={orderId}
                  kind={kind}
                />
              </ScrollContainer>
            )}
            <Footer orderId={orderId} customizations={customizations} />
          </RateProvider>
        )}

        {isThisCard(state, WAYPOINT_STATE_ARRIVED) && [
          <CompletedStatusBar />,
          !isOpen && <ArrivedCardHeader onToggleOpenCard={this.handleToggleCard} type={waypointType} />,
          isOpen && (
            <ScrollContainer>
              <ArrivedCardBody
                onToggleOpenCard={this.handleToggleCard}
                quiqee={quiqee}
                type={waypointType}
                customizations={customizations}
              />
            </ScrollContainer>
          ),
          <Footer orderId={orderId} customizations={customizations} />,
        ]}

        {isThisCard(state, WAYPOINT_STATE_PENDING) && [
          <LoadingBar isCancelled={jobState === ORDER_STATE_CANCELLED} />,
          <PendingCardHeader
            eta={eta}
            type={waypointType}
            onToggleOpenCard={this.handleToggleCard}
            isCardOpen={isOpen}
            isMobile={deviceInfo.isPhone}
            venueName={venueName}
            jobState={jobState}
            numberOfWaypointsAhead={numberOfWaypointsAhead}
            customizations={customizations}
          />,
          <ScrollContainer>
            <PendingCardBody
              quiqee={quiqee}
              jobState={jobState}
              waypoint={waypoint}
              type={waypointType}
              customizations={customizations}
            />
          </ScrollContainer>,
          <Footer orderId={orderId} customizations={customizations} />,
        ]}

        {isThisCard(state, WAYPOINT_STATE_DELIVERY_FAILED) && [
          <LoadingBar isCancelled={true} />,
          <DeliveryFailedHeader
            onToggleOpenCard={this.handleToggleCard}
            isCardOpen={isOpen}
            isMobile={deviceInfo.isPhone}
            customizations={customizations}
          />,
          <ScrollContainer>
            <DeliveryFailedBody waypoint={waypoint} />
          </ScrollContainer>,
          <Footer orderId={orderId} customizations={customizations} />,
        ]}
      </Container>
    );
  }
}

export default props => <DeviceInfo>{deviceInfo => <OrderCard deviceInfo={deviceInfo} {...props} />}</DeviceInfo>;



// WEBPACK FOOTER //
// ./src/app/order-card/OrderCard.js